import React, { useEffect, useCallback } from "react";

import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Services

import {
  consultaListasInternas,
  consultaListasInternacionales,
  clearConsultaListasInternas,
  clearConsultaListasInternacionales,
  clearInsertarClienteEnListaParaRevision,
  insertarClienteEnListaParaRevision,
} from "../../../store/lists/lists.actions";
import { getDropDownOptions } from "../../../store/options/options.actions";
import { consultaProductoActivo, clearConsultaProductoActivoStore } from '../../../store/accounts/accounts.actions';
import { handleSetConsultFormValues } from '../../../store/forms/forms.actions';

// Hooks

import InsertClientpartialData from '../../../hooks/InsertClientpartialData';

// Components

import TextInput from "../../UI/FormInput/TextInput";
import FormInputGroup from "../../UI/FormInputGroup/FormInputGroup";
import DropDownInput from "../../UI/FormInput/DropDownInput";
import MaskInput from "../../UI/FormInput/MaskInput";
import CalendarInput from "../../UI/FormInput/CalendarInput";
import RadioInput from '../../UI/FormInput/RadioInput';
import Button from "../../UI/Button/Button";
import Loader from "../../UI/Loader/Loader";

// Validation

import { validationSchema } from "./ValidationSchema";

// Utilities

import { formatDropDownOptions, formatDropDownOptionsNationality, getCurrentDateMinusYears } from "../../../utilities/helper-functions";

// Constants

import { IDENTIFICATION_MASK_FORMAT, PASSPORT_MASK_FORMAT, CLIENT_LISTS } from "../../../config/constants";
import Alert from "../../UI/Alert/Alert";

export default function ConsultListsForm({ state }) {
  const { insertPartialDataIsLoading, insertPartialDataError, insertPartialData, handleInsertPartialData } = InsertClientpartialData();
  // State
  const dropDownOptions = useSelector((state) => state.options.dropDownOptions);
  const consultFormValues = useSelector((state) => state.forms.consultFormValues);
  const forms = useSelector((state) => state.forms);
  const consultIfClientHasActiveProducts = useSelector((state) => state.accounts.consultIfClientHasActiveProducts);
  const consultInternalLists = useSelector((state) => state.lists.consultInternalLists);
  const consultInternationalLists = useSelector((state) => state.lists.consultInternationalLists);
  const insertClientOnListForReview = useSelector((state) => state.lists.insertClientOnListForReview);

  const dispatch = useDispatch();
  const navigate = useNavigate();


  // Setup form with formik

  const formik = useFormik({
    initialValues: consultFormValues ? consultFormValues : {
      cliente: {
        nombre: '',
        primerApellido: '',
        segundoApellido: '',
        fechaNacimiento: '',
        tipoDocumento: '',
        cedulaRif: '',
        pasaporte: '',
        fechaExpedicionIdentificacion: '',
        fechaVenceIdentificacion: '',
        nacionalidad: '',
        estadoCivil: '',
      },
    },
    validationSchema: validationSchema,
    // enableReinitialize: true,
    onSubmit: (values, actions) => {
      handelCheckIfHasActiveProducts(values, actions);
    },
  });

  useEffect(() => {
    dispatch(clearConsultaProductoActivoStore());
    dispatch(clearConsultaListasInternas());
    dispatch(clearConsultaListasInternacionales());
    dispatch(clearInsertarClienteEnListaParaRevision());
    dispatch(getDropDownOptions());
  }, [dispatch]);


  // Handle Check if the user already exists

  const handelCheckIfHasActiveProducts = (values, actions) => {
    const documentId = values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.tipoDocumento + values?.cliente?.cedulaRif : values?.cliente?.tipoDocumento + values?.cliente?.pasaporte;
    const data = {
      DNI: documentId
    };
    if (data) {
      dispatch(consultaProductoActivo(data));
    }

  }


  // Handle Consult Internal Lists

  const handleConsultInternalLists = useCallback((values) => {
    const data = {
      DNI: values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.tipoDocumento + values?.cliente?.cedulaRif : values?.cliente?.tipoDocumento + values?.cliente?.pasaporte,
    };
    dispatch(consultaListasInternas(data));
  }, []);

  useEffect(() => {
    if (consultIfClientHasActiveProducts?.hasActiveProduct === false) {
      handleConsultInternalLists(formik?.values);
    }
  }, [consultIfClientHasActiveProducts?.hasActiveProduct, handleConsultInternalLists]);

  useEffect(() => {
    if (consultInternalLists?.error?.codigoError === "1") {
      handleInsertarClienteEnListaParaRevision();
    } else if (consultInternalLists?.data?.codigoError === "0") {
      handleConsultInternationalLists(formik?.values);
    }
  }, [consultInternalLists, navigate]);


  // Handle Consult International Lists

  const handleConsultInternationalLists = useCallback((values) => {
    const data = {
      nombre: values?.cliente?.nombre + " " + values?.cliente?.primerApellido + " " + values?.cliente?.segundoApellido,
      identifiacion: values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.tipoDocumento + values?.cliente?.cedulaRif : values?.cliente?.tipoDocumento + values?.cliente?.pasaporte,
    };
    dispatch(consultaListasInternacionales(data));
  }, []);


  // Handle Setup Partial Data

  const handleSetupPartialData = (values, email, phone) => {
    return {
      email: email,
      is_onboarding: true,
      module: 0,
      step: 0,
      documentId: values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.tipoDocumento + values?.cliente?.cedulaRif : values?.cliente?.tipoDocumento + values?.cliente?.pasaporte,
      data: null,
      nombre: values?.cliente?.nombre,
      primerApellido: values?.cliente?.primerApellido,
      segundoApellido: values?.cliente?.segundoApellido,
      tipoDocumento: values?.cliente?.tipoDocumento,
      cedulaRif: values?.cliente?.tipoDocumento !== 'P' ? values?.cliente?.cedulaRif : '',
      pasaporte: values?.cliente?.tipoDocumento === 'P' ? values?.cliente?.pasaporte : '',
      fechaNacimiento: values?.cliente?.fechaNacimiento,
      fechaExpedicionIdentificacion: values?.cliente?.fechaExpedicionIdentificacion,
      fechaVenceIdentificacion: values?.cliente?.fechaVenceIdentificacion,
      nacionalidad: values?.cliente?.nacionalidad,
      estadoCivil: values?.cliente?.estadoCivil,
      tipoCliente: values?.cliente?.tipoCliente,
      phone: phone,
      products: [],
      selected_product: false,
      checked_list: true,
    }
  }


  // Handle Insert Client Partial Data

  const handleInsertClientPartialData = useCallback((values) => {
    const clientEmail = localStorage.getItem("clientEmail");
    const clientPhone = localStorage.getItem('clientPhone');

    if (clientEmail && !state?.userPartialData?.checked_list) {
      const data = handleSetupPartialData(values, clientEmail, clientPhone);
      handleInsertPartialData(data);
    } else {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (consultInternationalLists?.error?.codigoError === "1") {
      handleInsertarClienteEnListaParaRevision();
    } else if (consultInternationalLists?.data?.codigoError === "0") {
      handleInsertClientPartialData(formik?.values);
    }
  }, [consultInternationalLists]);


  // Handle Insert Client On List For Review

  const handleInsertarClienteEnListaParaRevision = async () => {
    const clientEmail = localStorage.getItem("clientEmail");

    if (clientEmail) {
      const data = {
        full_name: formik?.values?.cliente?.nombre + " " + formik?.values?.cliente?.primerApellido + " " + formik?.values?.cliente?.segundoApellido,
        email: clientEmail,
        document: formik?.values?.cliente?.tipoDocumento !== 'P' ? formik?.values?.cliente?.tipoDocumento + formik?.values?.cliente?.cedulaRif : formik?.values?.cliente?.tipoDocumento + formik?.values?.cliente?.pasaporte,
        font: consultInternationalLists?.error?.codigoError === "1" ? CLIENT_LISTS.internacional : CLIENT_LISTS.interna,
        type: "nuevo",
        codigo_mis: null,
      };
      dispatch(insertarClienteEnListaParaRevision(data));
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    if (insertClientOnListForReview?.data) {
      handleInsertClientPartialData(formik?.values);
    }
  }, [insertClientOnListForReview?.data]);

  useEffect(() => {
    if (insertPartialData && (consultInternalLists?.error?.codigoError === "1" || consultInternationalLists?.error?.codigoError === "1")) {

      navigate('/solicitud', { state: { userPartialData: { is_blocked: true } } });

    } else if (insertPartialData && consultInternalLists?.data?.codigoError === "0" && consultInternationalLists?.data?.codigoError === "0") {
      const clientEmail = localStorage.getItem("clientEmail");
      const clientPhone = localStorage.getItem('clientPhone');

      dispatch(handleSetConsultFormValues(formik.values));

      dispatch(clearConsultaProductoActivoStore());
      dispatch(clearConsultaListasInternas());
      dispatch(clearConsultaListasInternacionales());

      navigate('/tipocuenta', {
        state: {
          documentId: formik?.values?.cliente?.tipoDocumento !== 'P' ? formik?.values?.cliente?.tipoDocumento + formik?.values?.cliente?.cedulaRif : formik?.values?.cliente?.tipoDocumento + formik?.values?.cliente?.pasaporte,
          userPartialData: handleSetupPartialData(formik?.values, clientEmail, clientPhone)
        }
      });

    }
  }, [insertPartialData]);

  return (
    <>
      {(dropDownOptions?.isLoading || dropDownOptions?.error) && (
        <Loader
          message={"Cargando..."}
          error={dropDownOptions?.error}
          action={() => dispatch(getDropDownOptions())}
        />
      )}

      {!dropDownOptions?.isLoading && !dropDownOptions?.error && (
        <form onSubmit={formik.handleSubmit}>

          {!consultIfClientHasActiveProducts?.hasActiveProduct &&
            <Alert text={<strong>Para completar tu registro, por favor ingresa los datos solicitados.</strong>} />}

          {/* Form Error Message */}

          {(consultInternationalLists.error && consultInternationalLists.error?.codigoError !== "1") && (
            <p className="form-error-message">
              {consultInternationalLists?.error?.descripcionError || consultInternationalLists?.error}
            </p>
          )}

          {(consultInternalLists.error && consultInternalLists.error?.codigoError !== "1") && (
            <p className="form-error-message">
              {consultInternalLists.error?.descripcionError || consultInternalLists?.error}
            </p>
          )}

          {consultIfClientHasActiveProducts?.error && <p className="form-error-message">{consultIfClientHasActiveProducts?.error}</p>}

          {insertPartialDataError && <p className="form-error-message">{insertPartialDataError}</p>}

          {consultIfClientHasActiveProducts?.hasActiveProduct &&
            <Alert text={
              <span>
                Usted ya es cliente Bancaribe, debe iniciar sesión en el siguiente link con su usuario y clave de conexión: <Link to={'/'}><strong>Iniciar sesión</strong></Link>.
              </span>
            } />}

          <FormInputGroup>
            <TextInput
              formik={formik}
              fieldName={'cliente.nombre'}
              label={'Nombres'}
              maxLength={42}
              isRequired={true}
              isAlphabetic
            />

            <TextInput
              formik={formik}
              fieldName={'cliente.primerApellido'}
              label={'Primer Apellido'}
              maxLength={20}
              isRequired={true}
              isAlphabetic
            />

            <TextInput
              formik={formik}
              fieldName={'cliente.segundoApellido'}
              label={'Segundo Apellido'}
              maxLength={20}
              isAlphabetic
            />

            <CalendarInput
              formik={formik}
              fieldName={'cliente.fechaNacimiento'}
              label={'Fecha de Nacimiento'}
              minDate={getCurrentDateMinusYears(90)}
              maxDate={getCurrentDateMinusYears(10)}
              isRequired={true}
            />

            <DropDownInput
              formik={formik}
              fieldName={'cliente.tipoDocumento'}
              label={'Tipo de Identificación'}
              options={formatDropDownOptions(dropDownOptions?.data?.identificationTypes)}
              isRequired={true}
            />

            {(formik.values.cliente?.tipoDocumento && formik.values.cliente?.tipoDocumento !== 'P') &&
              <MaskInput
                formik={formik}
                fieldName={'cliente.cedulaRif'}
                label={'Número de Identificación'}
                mask={IDENTIFICATION_MASK_FORMAT}
                isRequired={true}
              />}

            {formik.values.cliente?.tipoDocumento === 'P' &&
              <MaskInput
                formik={formik}
                fieldName={'cliente.pasaporte'}
                label={'Número de Pasaporte'}
                mask={PASSPORT_MASK_FORMAT}
                isRequired={true}
              />}

            {formik.values.cliente?.tipoDocumento &&
              <CalendarInput
                formik={formik}
                fieldName={'cliente.fechaExpedicionIdentificacion'}
                label={'Fecha Expedición Identificación'}
                maxDate={new Date()}
                isRequired={true}
              />}

            {formik.values.cliente?.tipoDocumento &&
              <CalendarInput
                formik={formik}
                fieldName={'cliente.fechaVenceIdentificacion'}
                label={'Fecha Vencimiento Identificación'}
                minDate={formik.values.cliente?.tipoDocumento == 'P' ? new Date() : null}
                isRequired={true}
              />}

            <DropDownInput
              formik={formik}
              fieldName={'cliente.nacionalidad'}
              label={'Nacionalidad'}
              options={formatDropDownOptionsNationality(dropDownOptions?.data?.nationalities)}
              isRequired={true}
            />

            <DropDownInput
              formik={formik}
              fieldName={'cliente.estadoCivil'}
              label={'Estado Civil'}
              options={formatDropDownOptions(dropDownOptions?.data?.civilStatus)}
              isRequired={true}
              disabled={!formik.values?.cliente?.fechaNacimiento || formik.values?.cliente?.esMenorDeEdad}
            />

            <DropDownInput
              formik={formik}
              fieldName={'cliente.tipoCliente'}
              label={'Condición de cliente'}
              options={formatDropDownOptions(dropDownOptions?.data?.customerTypes)}
              isRequired={true}
              disabled={!formik.values?.cliente?.fechaNacimiento}
            />
          </FormInputGroup>

          {formik.values?.cliente?.tipoCliente === '2' &&
            <Alert text={<strong>Estimado cliente, si su única fuente de generación de ingresos es a través de su negocio propio, lo invitamos a solicitar el producto “Mi Cuenta Corriente”</strong>} />}

          <RadioInput
            formik={formik}
            fieldName={'terms'}
            label={''}
            options={[{ label: 'He leído mi información y certifico que está correcta. *', value: 'si' }]}
            isGroup={true}
          />

          <div className="form-actions">
            <Button
              type="submit"
              action={formik.handleSubmit}
              label={"Continuar"}
              isLoading={
                consultIfClientHasActiveProducts?.isLoading ||
                consultInternationalLists?.isLoading ||
                consultInternalLists?.isLoading ||
                insertClientOnListForReview?.isLoading ||
                insertPartialDataIsLoading
              }
            />
          </div>
        </form>
      )}
    </>
  );
}
